import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../api/firebase";
import { subscribeGiftCardSummaries } from "../services/giftCardSummaryService";
import { JOURNEY_CATEGORIES, summaryMatchesJourney } from "../constants/giftJourney";
import "../styles/home.css";
import "../styles/journey.css";

export default function Home() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db) {
      setSummaries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeGiftCardSummaries((rows) => {
      setSummaries(rows);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const byOccasion = useMemo(() => {
    const map = {};
    JOURNEY_CATEGORIES.forEach((cat) => {
      map[cat.slug] = summaries.filter((s) => summaryMatchesJourney(s, cat.slug));
    });
    return map;
  }, [summaries]);

  const homeOccasionTiles = useMemo(
    () => JOURNEY_CATEGORIES.filter((cat) => cat.slug !== "egift"),
    []
  );

  const scrollToOccasions = () => {
    document.getElementById("choose-occasion")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFooter = () => {
    document.getElementById("footer-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="home-page">
      {!db && (
        <div className="home-firebase-banner" role="status">
          <strong>Service temporarily unavailable.</strong> We are reconnecting the catalog.
        </div>
      )}

      <section className="home-hero" aria-labelledby="hero-heading">
        <div className="home-hero-inner">
          <span className="home-hero-badge">Digital e-cards · Instant delivery</span>
          <h1 id="hero-heading">Send a memorable e-card in one link</h1>
          <p>
            Pick an occasion and choose a beautiful design in seconds.
          </p>
          <div className="home-hero-actions">
            <button type="button" className="home-btn-primary" onClick={scrollToOccasions}>
              Browse occasions
            </button>
            <button type="button" className="home-btn-ghost" onClick={scrollToFooter}>
              Contact us
            </button>
          </div>
        </div>
      </section>

      <section
        id="choose-occasion"
        className="home-occasion-block home-occasion-block--primary"
        data-home-layout="tiles-only"
      >
        <div className="home-section-head home-section-head--spaced">
          <div>
            <h2>Browse by occasion</h2>
            <span>Open a design instantly and personalize your message.</span>
          </div>
        </div>

        <div className="journey-cats journey-cat" data-source="firebase-gifts">
          {homeOccasionTiles.map((cat) => {
            const rows = byOccasion[cat.slug] || [];
            const count = rows.length;
            const first = rows[0];
            const to = first
              ? `/product/${encodeURIComponent(first.id)}?occasion=${encodeURIComponent(cat.slug)}`
              : `/browse/${cat.slug}`;
            const meta = !db
              ? "Catalog is reconnecting."
              : loading
                ? "Loading designs…"
                : `${count} ${count === 1 ? "design" : "designs"} available`;
            return (
              <Link
                key={cat.slug}
                id={`occ-${cat.slug}`}
                to={to}
                className="journey-tile"
              >
                <div className="journey-tile-emoji" aria-hidden>
                  {cat.emoji}
                </div>
                <h3>{cat.label}</h3>
                <p>{cat.description}</p>
                <p
                  className={`journey-tile-meta${!db ? " journey-tile-meta--muted" : ""}`}
                  aria-live="polite"
                >
                  {meta}
                </p>
                <div className="journey-tile-cta">
                  {first ? "Open design →" : "Browse designs →"}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
